import { useUIStore } from "@/store/ui/ui-store";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { BsWindow, BsBank } from "react-icons/bs";
import { CiCalendarDate, CiSettings } from "react-icons/ci";
import { FaHospitalUser, FaUsersCog, FaCog, FaChartBar, FaUserShield, FaUserPlus, FaUserTimes, FaCogs, FaMoneyBillWave, FaFileInvoice, FaCloudDownloadAlt, FaCloudUploadAlt, FaSchool } from "react-icons/fa";
import { FaArrowsToEye, FaUsersBetweenLines, FaMoneyBill } from "react-icons/fa6";
import { MdEmail, MdFamilyRestroom, MdKeyboardArrowDown, MdTableRestaurant, MdOutlineDashboard, MdWork, MdAssessment, MdPayment, MdAccountBalance } from "react-icons/md";
import { PiStudentFill, PiStudentLight } from "react-icons/pi";
import { RiDashboard2Line } from "react-icons/ri";
import { SlEarphonesAlt } from "react-icons/sl";
import { TbReportSearch } from "react-icons/tb";
import { GiSkills } from "react-icons/gi";
import { AiOutlineClockCircle } from "react-icons/ai";
import { FiUsers } from "react-icons/fi";

const ICONS: { [key: string]: JSX.Element } = {
    prueba: <FaArrowsToEye />,
    setting: <CiSettings />,
    module: <BsWindow />,
    school: <FaSchool />,
    table: <MdTableRestaurant />,
    family: <MdFamilyRestroom />,
    student: <PiStudentFill />,
    calendar: <CiCalendarDate />,
    report: <RiDashboard2Line />,
    users: <FaUsersCog />,
    userAdd: <FaUserPlus />,
    user: <FaHospitalUser />,
    tableStudents: <FaUsersBetweenLines />,
    email: <MdEmail />,
    comunication: <SlEarphonesAlt />,
    dashboard: <MdOutlineDashboard />,
    reportSearch: <TbReportSearch />,
    economic: <FaMoneyBill />,
    process: <FaCog />,
    seccionStudent: <PiStudentLight />,
    work: <MdWork />,
    horarioPersonal: <GiSkills />,
    asistencia: <AiOutlineClockCircle />,
    reportGlobal: <MdAssessment />,
    reportSede: <FaChartBar />,
    userRol: <FaUserShield />,
    userComportamiento: <FiUsers />,
    userDeudores: <FaUserTimes />,
    administration: <FaCogs />,
    tesoreria: <FaMoneyBillWave />,
    payment: <MdPayment />,
    reserverPayment: <MdAccountBalance />,
    reportPayment: <FaFileInvoice />,
    bank: <BsBank />,
    download: <FaCloudDownloadAlt />,
    upload: <FaCloudUploadAlt />,
};

interface SubMenuItem {
    id: number;
    label: string;
    icon: string;
    link: string;
    subMenu: any[];
}

interface Props {
    label: string;
    subMenu: SubMenuItem[];
    icons: string;
    /** true en submenús anidados (nivel 2): guía vertical + más sangría. */
    nested?: boolean;
    /** Sidebar contraído a riel de iconos (solo nivel 1). */
    collapsed?: boolean;
}

const pathInTree = (items: any[] = [], path: string): boolean =>
    items.some((it) => it.link === path || pathInTree(it.subMenu, path));

export const SubMenu = ({ label, subMenu, icons, nested = false, collapsed = false }: Props) => {
    const path = usePathname();
    const closeMenu = useUIStore((state) => state.closeSideMenu);
    const expandSidebar = useUIStore((state) => state.setSideMenuCollapsed);

    const hasActiveChild = pathInTree(subMenu, path);
    const [open, setOpen] = useState(hasActiveChild);

    const handleHeaderClick = () => {
        if (collapsed) {
            // No hay espacio para desplegar en el riel: expande el sidebar y abre.
            expandSidebar(false);
            setOpen(true);
            return;
        }
        setOpen((v) => !v);
    };

    return (
        <div>
            <button
                type="button"
                onClick={handleHeaderClick}
                title={collapsed ? label : undefined}
                aria-expanded={open}
                className={`group flex w-full items-center rounded-lg text-xs transition-colors motion-reduce:transition-none
                    ${collapsed ? "justify-center p-2.5" : "justify-between p-2"}
                    ${
                        hasActiveChild
                            ? "text-[var(--nav-active-fg)]"
                            : "text-[var(--nav-fg-strong)] hover:bg-[var(--nav-hover-bg)]"
                    }`}
            >
                <span className="flex min-w-0 items-center gap-2.5">
                    <span className={`shrink-0 ${nested ? "text-[15px]" : "text-[17px]"}`}>
                        {ICONS[icons]}
                    </span>
                    {!collapsed && <span className="truncate font-medium">{label}</span>}
                </span>
                {!collapsed && (
                    <MdKeyboardArrowDown
                        size={16}
                        className={`shrink-0 transition-transform duration-300 motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
                    />
                )}
            </button>

            {!collapsed && (
                <div className="nav-collapse" data-open={open}>
                    <div>
                        <ul
                            className={`nav-collapse__inner mt-0.5 flex flex-col gap-0.5 ${
                                nested
                                    ? "ml-[19px] border-l border-[var(--nav-guide)] pl-3"
                                    : "pl-3"
                            }`}
                        >
                            {subMenu.map((item) =>
                                item?.subMenu?.length > 0 ? (
                                    <li key={item.id}>
                                        <SubMenu
                                            label={item.label}
                                            subMenu={item.subMenu}
                                            icons={item.icon}
                                            nested
                                        />
                                    </li>
                                ) : (
                                    <li key={item.id}>
                                        <Link
                                            onClick={closeMenu}
                                            href={item.link}
                                            className={`relative flex items-center gap-2.5 rounded-lg py-2 pl-3 pr-2 text-xs transition-colors motion-reduce:transition-none
                                                ${
                                                    path === item.link
                                                        ? "bg-[var(--nav-active-bg)] font-medium text-[var(--nav-active-fg)] before:absolute before:inset-y-1 before:left-0 before:w-0.5 before:rounded-full before:bg-[var(--nav-accent)]"
                                                        : "text-[var(--nav-fg)] hover:bg-[var(--nav-hover-bg)] hover:text-[var(--nav-fg-strong)]"
                                                }`}
                                        >
                                            <span className="shrink-0 text-[15px]">{ICONS[item.icon]}</span>
                                            <span className="truncate">{item.label}</span>
                                        </Link>
                                    </li>
                                )
                            )}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};
